/**
 * @page /portal/dokumente
 * @description Portal-Dokumente — Lohnzettel, persoenliche und allgemeine Dokumente verwalten.
 *   Mitarbeiter koennen Dokumente herunterladen, Admins koennen hochladen.
 * @features
 *   - Lohnzettel-Tab (monatliche Gehaltsabrechnungen)
 *   - Persoenliche Dokumente (Dienstvertrag, etc.)
 *   - Allgemeine Dokumente (Formulare, Schulungen, Sicherheit)
 *   - Kategorie-Filter und Download
 *   - Upload-Funktion fuer Admins/Manager
 *   - Lesebestaetigung fuer allgemeine Dokumente
 * @api GET /api/portal/dokumente, GET /api/portal/allgemeine-dokumente
 * @api GET /api/portal/dokumente/:id/download, GET /api/portal/allgemeine-dokumente/:id/download
 * @api POST /api/portal/allgemeine-dokumente/:id/gelesen
 * @api GET /api/portal/lohnzettel, GET /api/portal/lohnzettel/:id/download
 * @api GET /api/portal/mitarbeiter-liste
 * @roles mitarbeiter, admin, administrator, ops_manager
 * @i18n portal.docs, portal.documents, portal.lohnzettel
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { FileText, FolderOpen, Download, Calendar, ClipboardList, CheckCircle, Inbox } from 'lucide-react';
import { PageHeader } from '../components/ui';

const API = import.meta.env.VITE_API_URL || '';

const MA_KAT_KEYS = {
  lohnzettel: 'portal.docs.catPayslip',
  betriebsvereinbarung: 'portal.docs.catCompanyAgreement',
  onboarding: 'portal.docs.catOnboarding',
  sonstiges: 'portal.docs.catOther',
};

const ALLG_KAT_KEYS = {
  allgemein: 'portal.docs.catGeneral',
  onboarding: 'portal.docs.catOnboarding',
  formulare: 'portal.docs.catForms',
  sicherheit: 'portal.docs.catSafety',
  schulungen: 'portal.docs.catTraining',
};

const MONAT_DE = ['', 'Jänner', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const MONAT_EN = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatEur(val) {
  if (val == null) return '';
  const n = Number(val);
  return n >= 10 ? `€ ${Math.round(n).toLocaleString('de-DE')}` : `€ ${n.toFixed(1)}`;
}

function DokumentRow({ doc, onDownload, t }) {
  return (
    <div className="flex items-center gap-3 py-3 border-b border-line/30 last:border-0 group">
      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#338edc]/10 flex items-center justify-center">
        <FileText className="w-4 h-4 text-[#338edc]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-content truncate">{doc.titel}</div>
        <div className="text-[11px] text-[var(--text-tertiary)] mt-0.5 flex items-center gap-2">
          <span>{formatDate(doc.created_at)}</span>
          {doc.datei_groesse && <span>· {formatSize(doc.datei_groesse)}</span>}
          {doc.beschreibung && <span>· {doc.beschreibung}</span>}
        </div>
      </div>
      <button
        onClick={() => onDownload(doc.id, doc.datei_name)}
        className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#338edc]/10 text-[#338edc] hover:bg-[#338edc]/20 transition-colors opacity-70 group-hover:opacity-100"
      >
        {t('portal.docs.download')}
      </button>
    </div>
  );
}

function Accordion({ title, count, children, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <div className="border border-line rounded-card overflow-hidden mb-3">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-surface-card hover:bg-surface-hover transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className={`text-xs transition-transform ${open ? 'rotate-90' : ''}`}>&#9654;</span>
          <span className="text-sm font-semibold text-content">{title}</span>
        </div>
        <span className="px-2 py-0.5 rounded-full bg-surface-inset text-[10px] font-bold text-[var(--text-tertiary)]">
          {count}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-2 bg-surface-card border-t border-line/30">
          {children}
        </div>
      )}
    </div>
  );
}

export default function MaPortalDokumente() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [tab, setTab] = useState('lohnzettel');
  const [meineDoks, setMeineDoks] = useState([]);
  const [allgDoks, setAllgDoks] = useState([]);
  const [lohnzettel, setLohnzettel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mitarbeiterListe, setMitarbeiterListe] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadTab, setUploadTab] = useState('meine'); // meine | allgemein
  const [uploadData, setUploadData] = useState({ mitarbeiter_id: '', kategorie: 'sonstiges', titel: '', beschreibung: '' });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [vertraege, setVertraege] = useState([]);

  const isAdmin = ['admin', 'administrator', 'ops_manager'].includes(user?.rolle);
  const isAdminOrManager = ['admin', 'administrator', 'manager', 'ops_manager', 'teamleiter'].includes(user?.rolle);

  const loadMeineDoks = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/portal/dokumente`, { credentials: 'include' });
      if (res.ok) setMeineDoks(await res.json());
    } catch (err) { console.error(err); }
  }, []);

  const loadAllgDoks = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/portal/allgemeine-dokumente`, { credentials: 'include' });
      if (res.ok) setAllgDoks(await res.json());
    } catch (err) { console.error(err); }
  }, []);

  const loadLohnzettel = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/portal/lohnzettel`, { credentials: 'include' });
      if (res.ok) setLohnzettel(await res.json());
    } catch (err) { console.error(err); }
  }, []);

  const loadMitarbeiter = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await fetch(`${API}/api/portal/mitarbeiter-liste`, { credentials: 'include' });
      if (res.ok) setMitarbeiterListe(await res.json());
    } catch (err) { console.error(err); }
  }, [isAdmin]);

  const loadVertraege = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/portal/vertraege`, { credentials: 'include' });
      if (res.ok) setVertraege(await res.json());
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    Promise.all([loadMeineDoks(), loadAllgDoks(), loadLohnzettel(), loadMitarbeiter(), loadVertraege()]).finally(() => setLoading(false));
  }, [loadMeineDoks, loadAllgDoks, loadLohnzettel, loadMitarbeiter, loadVertraege]);

  // Gruppierung
  const meineGruppiert = useMemo(() => {
    const groups = {};
    for (const doc of meineDoks) {
      const kat = doc.kategorie || 'sonstiges';
      if (!groups[kat]) groups[kat] = [];
      groups[kat].push(doc);
    }
    return groups;
  }, [meineDoks]);

  const allgGruppiert = useMemo(() => {
    const groups = {};
    for (const doc of allgDoks) {
      const kat = doc.kategorie || 'allgemein';
      if (!groups[kat]) groups[kat] = [];
      groups[kat].push(doc);
    }
    return groups;
  }, [allgDoks]);

  // Lohnzettel grouped by year
  const lohnzettelGruppiert = useMemo(() => {
    const groups = {};
    for (const doc of lohnzettel) {
      const year = doc.gueltig_ab ? new Date(doc.gueltig_ab).getFullYear() : 'Sonstige';
      if (!groups[year]) groups[year] = [];
      groups[year].push(doc);
    }
    return groups;
  }, [lohnzettel]);

  const lohnzettelJahre = useMemo(() =>
    Object.keys(lohnzettelGruppiert).sort((a, b) => b - a),
    [lohnzettelGruppiert]
  );

  const monatNames = language === 'de' ? MONAT_DE : MONAT_EN;

  const getMonatLabel = (doc) => {
    if (!doc.gueltig_ab) return '';
    const m = new Date(doc.gueltig_ab).getMonth() + 1;
    return monatNames[m] || '';
  };

  const handleDownloadMeine = async (id, filename) => {
    try {
      const res = await fetch(`${API}/api/portal/dokumente/${id}/download`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDownloadAllg = async (id, filename) => {
    try {
      const res = await fetch(`${API}/api/portal/allgemeine-dokumente/${id}/download`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDownloadLohnzettel = async (doc) => {
    try {
      const res = await fetch(`${API}/api/portal/lohnzettel/${doc.id}/download`, { credentials: 'include' });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.datei_name || 'Lohnzettel.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    }
  };

  const handleLesebestaetigung = async (id) => {
    try {
      await fetch(`${API}/api/portal/allgemeine-dokumente/${id}/gelesen`, { method: 'POST', credentials: 'include' });
      loadAllgDoks();
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('titel', uploadData.titel || uploadFile.name);
      formData.append('beschreibung', uploadData.beschreibung || '');
      formData.append('kategorie', uploadData.kategorie);

      let url;
      if (uploadTab === 'meine') {
        formData.append('mitarbeiter_id', uploadData.mitarbeiter_id);
        url = `${API}/api/portal/dokumente`;
      } else {
        formData.append('sichtbar_fuer', 'alle');
        formData.append('lesebestaetigung_noetig', uploadData.lesebestaetigung_noetig ? 'true' : 'false');
        url = `${API}/api/portal/allgemeine-dokumente`;
      }

      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (res.ok) {
        setShowUpload(false);
        setUploadFile(null);
        setUploadData({ mitarbeiter_id: '', kategorie: 'sonstiges', titel: '', beschreibung: '' });
        loadMeineDoks();
        loadAllgDoks();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const TABS = [
    { key: 'lohnzettel', label: t('portal.documents.payslips', 'Payslips') },
    { key: 'meine', label: t('portal.documents.personal', 'Personal Documents') },
    { key: 'allgemein', label: t('portal.documents.general', 'General Documents') },
    { key: 'vertraege', label: t('portal.documents.contracts', 'Contracts') },
  ];

  if (loading) {
    return (
      <div className="p-page">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-inset rounded w-1/3" />
          <div className="h-48 bg-surface-inset rounded-card" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-section">
        <PageHeader title={t('portal.docs.title')} />
        {isAdmin && (
          <button
            onClick={() => setShowUpload(true)}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors"
          >
            {t('portal.docs.upload')}
          </button>
        )}
      </div>

      {/* Tabs — SKILL-design pattern */}
      <div className="flex gap-1 mb-section bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)]">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex-1 px-4 py-2 rounded-[8px] text-sm font-semibold transition-colors ${
              tab === key ? 'bg-white text-content shadow-sm' : 'text-[var(--text-tertiary)] hover:text-content'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <div className="bg-surface-card border border-[var(--border-default,#E5E7EB)] rounded-card p-5 mb-section">
          <h3 className="text-sm font-bold text-content mb-4">{t('portal.docs.uploadDocument')}</h3>

          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setUploadTab('meine')}
              className={`px-3 py-1 rounded text-xs font-semibold ${
                uploadTab === 'meine' ? 'bg-[#338edc] text-white' : 'bg-surface-inset text-[var(--text-secondary)]'
              }`}
            >
              {t('portal.docs.employeeDoc')}
            </button>
            <button
              onClick={() => setUploadTab('allgemein')}
              className={`px-3 py-1 rounded text-xs font-semibold ${
                uploadTab === 'allgemein' ? 'bg-[#338edc] text-white' : 'bg-surface-inset text-[var(--text-secondary)]'
              }`}
            >
              {t('portal.docs.generalDoc')}
            </button>
          </div>

          <div className="space-y-3">
            {uploadTab === 'meine' && (
              <select
                value={uploadData.mitarbeiter_id}
                onChange={e => setUploadData(prev => ({ ...prev, mitarbeiter_id: e.target.value }))}
                className="w-full px-3 py-2 bg-surface-inset border border-line rounded-lg text-sm text-content focus:outline-none"
              >
                <option value="">{t('portal.docs.selectEmployee')}</option>
                {mitarbeiterListe.map(ma => (
                  <option key={ma.id} value={ma.id}>{ma.nachname}, {ma.vorname} – {ma.department_name || ''}</option>
                ))}
              </select>
            )}

            <select
              value={uploadData.kategorie}
              onChange={e => setUploadData(prev => ({ ...prev, kategorie: e.target.value }))}
              className="w-full px-3 py-2 bg-surface-inset border border-line rounded-lg text-sm text-content focus:outline-none"
            >
              {Object.entries(uploadTab === 'meine' ? MA_KAT_KEYS : ALLG_KAT_KEYS).map(([k, tKey]) => (
                <option key={k} value={k}>{t(tKey)}</option>
              ))}
            </select>

            <input
              type="text"
              placeholder={t('portal.docs.titleOptional')}
              value={uploadData.titel}
              onChange={e => setUploadData(prev => ({ ...prev, titel: e.target.value }))}
              className="w-full px-3 py-2 bg-surface-inset border border-line rounded-lg text-sm text-content placeholder:text-[var(--text-tertiary)] focus:outline-none"
            />

            <input
              type="text"
              placeholder={t('portal.docs.descriptionOptional')}
              value={uploadData.beschreibung}
              onChange={e => setUploadData(prev => ({ ...prev, beschreibung: e.target.value }))}
              className="w-full px-3 py-2 bg-surface-inset border border-line rounded-lg text-sm text-content placeholder:text-[var(--text-tertiary)] focus:outline-none"
            />

            {uploadTab === 'allgemein' && (
              <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={uploadData.lesebestaetigung_noetig || false}
                  onChange={e => setUploadData(prev => ({ ...prev, lesebestaetigung_noetig: e.target.checked }))}
                  className="rounded"
                />
                {t('portal.docs.readConfirmRequired')}
              </label>
            )}

            {/* File Drop Zone */}
            <div
              className="border-2 border-dashed border-line rounded-card p-6 text-center cursor-pointer hover:border-[#338edc]/50 transition-colors"
              onClick={() => document.getElementById('file-upload').click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                e.preventDefault();
                if (e.dataTransfer.files[0]) setUploadFile(e.dataTransfer.files[0]);
              }}
            >
              <input
                type="file"
                id="file-upload"
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                onChange={e => { if (e.target.files[0]) setUploadFile(e.target.files[0]); }}
              />
              {uploadFile ? (
                <div className="text-sm text-content">
                  <FileText className="w-4 h-4 inline mr-1" />{uploadFile.name} ({formatSize(uploadFile.size)})
                </div>
              ) : (
                <div>
                  <FolderOpen className="w-8 h-8 text-[var(--text-tertiary)] mx-auto" />
                  <p className="text-sm text-[var(--text-secondary)] mt-2">{t('portal.docs.dragOrClick')}</p>
                  <p className="text-[11px] text-[var(--text-tertiary)] mt-1">{t('portal.docs.fileTypes')}</p>
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={handleUpload}
                disabled={uploading || !uploadFile || (uploadTab === 'meine' && !uploadData.mitarbeiter_id)}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors"
              >
                {uploading ? t('portal.docs.uploading') : t('portal.docs.uploadBtn')}
              </button>
              <button
                onClick={() => { setShowUpload(false); setUploadFile(null); }}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-surface-inset text-[var(--text-tertiary)] hover:text-content transition-colors"
              >
                {t('portal.docs.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lohnzettel Tab */}
      {tab === 'lohnzettel' && (
        <div>
          {lohnzettel.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-10 h-10 text-[var(--text-tertiary)] mx-auto" />
              <p className="text-[var(--text-secondary)] mt-3">{t('portal.lohnzettel.empty')}</p>
            </div>
          ) : (
            lohnzettelJahre.map(year => (
              <div key={year} className="mb-6">
                <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {year}
                </h2>
                <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
                  {lohnzettelGruppiert[year].map((doc, i) => (
                    <div
                      key={doc.id}
                      className={`flex items-center gap-3 px-4 py-3 hover:bg-[var(--bg-hover,#F9FAFB)] transition-colors ${i > 0 ? 'border-t border-[var(--border-default,#E5E7EB)]' : ''}`}
                    >
                      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#338edc]/10 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-[#338edc]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[var(--text-primary)] truncate">
                          {getMonatLabel(doc)}
                        </div>
                        <div className="text-xs text-[var(--text-tertiary)] flex items-center gap-2 flex-wrap">
                          {doc.netto != null && (
                            <span className="font-semibold text-[var(--text-secondary)]">
                              {t('portal.lohnzettel.netto')}: {formatEur(doc.netto)}
                            </span>
                          )}
                          {doc.brutto != null && (
                            <span>{t('portal.lohnzettel.brutto')}: {formatEur(doc.brutto)}</span>
                          )}
                          {doc.netto == null && (
                            <span>{formatSize(doc.datei_groesse)}</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownloadLohnzettel(doc)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-[#338edc] hover:bg-[#338edc]/10 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        {t('portal.lohnzettel.download')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Meine Dokumente */}
      {tab === 'meine' && (
        <div>
          {meineDoks.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-10 h-10 text-[var(--text-tertiary)] mx-auto" />
              <p className="text-[var(--text-secondary)] mt-3">{t('portal.docs.noMyDocs')}</p>
            </div>
          ) : (
            Object.entries(meineGruppiert).map(([kat, docs]) => (
              <Accordion
                key={kat}
                title={MA_KAT_KEYS[kat] ? t(MA_KAT_KEYS[kat]) : kat}
                count={docs.length}
                defaultOpen={docs.length <= 5}
              >
                {docs.map(doc => (
                  <DokumentRow key={doc.id} doc={doc} onDownload={handleDownloadMeine} t={t} />
                ))}
              </Accordion>
            ))
          )}
        </div>
      )}

      {/* Allgemeine Dokumente */}
      {tab === 'allgemein' && (
        <div>
          {allgDoks.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-10 h-10 text-[var(--text-tertiary)] mx-auto" />
              <p className="text-[var(--text-secondary)] mt-3">{t('portal.docs.noGeneralDocs')}</p>
            </div>
          ) : (
            Object.entries(allgGruppiert).map(([kat, docs]) => (
              <Accordion
                key={kat}
                title={ALLG_KAT_KEYS[kat] ? t(ALLG_KAT_KEYS[kat]) : kat}
                count={docs.length}
                defaultOpen
              >
                {docs.map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 py-3 border-b border-line/30 last:border-0 group">
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#338edc]/10 flex items-center justify-center">
                      <FileText className="w-4 h-4 text-[#338edc]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-content truncate">{doc.titel}</span>
                        {doc.lesebestaetigung_noetig && !doc.gelesen && (
                          <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 text-[9px] font-bold uppercase">
                            {t('portal.docs.pleaseRead')}
                          </span>
                        )}
                        {doc.gelesen && doc.lesebestaetigung_noetig && (
                          <span className="text-green-500 text-xs">{t('portal.docs.confirmed')}</span>
                        )}
                      </div>
                      <div className="text-[11px] text-[var(--text-tertiary)] mt-0.5 flex items-center gap-2">
                        <span>{formatDate(doc.created_at)}</span>
                        {doc.beschreibung && <span>· {doc.beschreibung}</span>}
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex gap-2">
                      {doc.lesebestaetigung_noetig && !doc.gelesen && (
                        <button
                          onClick={() => handleLesebestaetigung(doc.id)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors"
                        >
                          {t('portal.docs.markRead')}
                        </button>
                      )}
                      <button
                        onClick={() => handleDownloadAllg(doc.id, doc.datei_name)}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#338edc]/10 text-[#338edc] hover:bg-[#338edc]/20 transition-colors opacity-70 group-hover:opacity-100"
                      >
                        {t('portal.docs.download')}
                      </button>
                    </div>
                  </div>
                ))}
              </Accordion>
            ))
          )}
        </div>
      )}

      {/* Verträge Tab */}
      {tab === 'vertraege' && (
        <div>
          {vertraege.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-2xl bg-[var(--bg-inset,#F8F9FC)] flex items-center justify-center mb-4">
                <Inbox className="w-6 h-6 text-[var(--text-muted)]" />
              </div>
              <h3 className="text-base font-semibold text-[var(--text-primary)] mb-1">{t('portalContracts.noData')}</h3>
              <p className="text-sm text-[var(--text-tertiary)] max-w-sm">{t('portalContracts.noDataDesc')}</p>
            </div>
          ) : (
            <>
              {/* Mini KPIs */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 text-center">
                  <ClipboardList className="w-5 h-5 text-[#338edc] mx-auto mb-1" />
                  <div className="text-lg font-bold text-[var(--text-primary)]">{vertraege.length}</div>
                  <div className="text-[10px] text-[var(--text-tertiary)]">{t('portalContracts.contracts')}</div>
                </div>
                <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 text-center">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
                  <div className="text-lg font-bold text-[var(--text-primary)]">{vertraege.find(v => v.ist_aktiv || v.status === 'Laufend') ? t('portalContracts.active') : '–'}</div>
                  <div className="text-[10px] text-[var(--text-tertiary)]">{t('common.status')}</div>
                </div>
                <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 text-center">
                  <FileText className="w-5 h-5 text-[#338edc] mx-auto mb-1" />
                  <div className="text-lg font-bold text-[var(--text-primary)]">{vertraege.filter(v => v.pdf_pfad).length}</div>
                  <div className="text-[10px] text-[var(--text-tertiary)]">{t('portalContracts.documents')}</div>
                </div>
              </div>

              {/* Contract List */}
              <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
                {vertraege.map((v, i) => {
                  const isActive = v.ist_aktiv || v.status === 'Laufend';
                  const hasPdf = !!v.pdf_pfad;
                  const isDv = String(v.id).startsWith('dv-');
                  const dvId = isDv ? String(v.id).replace('dv-', '') : null;
                  const downloadUrl = isDv
                    ? `${API}/api/portal/digitale-vertraege/${dvId}/download`
                    : `${API}/api/portal/vertraege/${v.id}/download`;
                  return (
                    <div key={v.id} className={`flex items-center gap-3 px-4 py-3 ${isActive ? 'bg-emerald-500/5' : 'hover:bg-[var(--bg-inset,#F8F9FC)]'} transition-colors ${i > 0 ? 'border-t border-[var(--border-default,#E5E7EB)]' : ''}`}>
                      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#338edc]/10 flex items-center justify-center">
                        <ClipboardList className="w-4 h-4 text-[#338edc]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {isActive && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />}
                          <span className={`text-sm font-medium truncate ${isActive ? 'text-content' : 'text-[var(--text-secondary)]'}`}>
                            {v.bezeichnung || '–'}
                          </span>
                          <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
                            v.status === 'Laufend' ? 'bg-emerald-100 text-emerald-700' :
                            v.status === 'unterschrieben' ? 'bg-emerald-100 text-emerald-700' :
                            v.status === 'Entwurf' ? 'bg-amber-100 text-amber-700' :
                            'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary)]'
                          }`}>
                            {v.status || '–'}
                          </span>
                        </div>
                        <div className="text-[11px] text-[var(--text-tertiary)] mt-0.5 flex items-center gap-2">
                          <span>{formatDate(v.start_datum)}{v.end_datum ? ` – ${formatDate(v.end_datum)}` : ''}</span>
                          {v.stunden_pro_woche && <span>· {v.stunden_pro_woche} {t('portalContracts.hoursPerWeek')}</span>}
                        </div>
                      </div>
                      {hasPdf && (
                        <button
                          onClick={() => window.open(downloadUrl, '_blank')}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-[#338edc] hover:bg-[#338edc]/10 transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          PDF
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
